<template>
  <tr :class="handleTRCssClasses(item, canEdit)"
  :style="handleTRCssStyles(item)"
  @click="disableKeyboardFunc && handleClickedItem(item)"
  @dblclick="handleDoubleClick(item)"
  >
    <td v-if="showSelect" @click.stop>
      <v-layout row wrap>
        <v-checkbox  v-if="!handleDisableCheckboxes(item)"
        v-model="selectedTask" :value="item"
        class="pl-3 shrink ma-0" hide-details />
      </v-layout>
    </td>
    <td
      v-for="header in headerList" :key="header.value"
      :class="determineCssClasses(item, canEdit, header)"
    >
      <span v-if="header.formatter === 'file'"
        @click.stop
      >
        <UploadFile
        class="changeHeightSize"
          v-if=item.hasFiles
          :bucketName="uploadFileParams.bucketName"
          :program="uploadFileParams.program"
          :parentObjectType="uploadFileParams.parentObjectType"
          :parentObjectId="item._id"
          :itemIsSaved="true"
          :showText="false"
        ></UploadFile>
      </span>
      <span v-else-if="header.formatter === 'localDateTime'">
        {{toLocalDateTime(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'shortDate'">
        {{toShortDate(resolveSubProp(header.value, item))}}
      </span>

      <span v-else-if="header.formatter === 'amountCanbeBlank'"
      :style="{color: changeTxtColor(item, header)}">
        {{amountFormatCanbeEmpty(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'amount' && header.clickable"
      class="clickable-amount"
      :style="{color: changeTxtColor(item, header)}"
      @click="handleButtonClick(item)">
        {{amountFormat(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'amount'"
      :style="{color: changeTxtColor(item, header)}">
        {{amountFormat(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'amountOrZero'"
      :style="{color: changeTxtColor(item, header)}">
        {{ resolveSubProp(header.value, item) ?
         amountFormat(resolveSubProp(header.value, item)): ''}}
      </span>
      <span v-else-if="header.formatter === 'auto'">
        {{autoDetectFormat(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'getFirstChar'">
        {{getFirstChar(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'boolToStr'">
        {{boolToStr(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'customMethod'"
      :style="{color: changeTxtColor(item, header)}">
        {{header.methodDefinition(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'pId'">
        {{pIdFormat(resolveSubProp(header.value, item), formatPidPositions)}}
      </span>
      <span v-else-if="header.formatter === 'combineName'"
       :class="changeTheTextColor(item, header)">
        {{combinedNames(item, header)}}
      </span>
      <span v-else-if="header.formatter === 'shortenCharacter'"
      :title="shouldTitleDisplay(resolveSubProp(header.value, item))">
        {{shortenCharacterFormat(resolveSubProp(header.value, item),
         header, item)}}
      </span>
      <span v-else-if="header.formatter === 'flagToYorEmpty'">
        {{flagToYorEmpty(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'propertyTaxTypeShortCode'">
        {{propertyTaxTypeShortCode(resolveSubProp(header.value, item))}}
      </span>
      <span v-else-if="header.formatter === 'voidTransactionId'" v-html="voidTransactionId(resolveSubProp(header.value, item), item)">
      </span>
      <span v-else-if="header.formatter === 'hideAndShowFields'">
        {{hideAndShowFields(resolveSubProp(header.value, item))}}
      </span>
      <!-- A button column may carry showButtonIf - (item) => Boolean - so the cell can stay empty
           on the rows the button does not apply to, and buttonColor to stand out from the plain
           blue (see the large payment grid's per-row Add All Unpaids). A header with neither
           behaves as it always has - a blue button on every row. -->
      <span v-else-if="header.formatter === 'button'">
        <v-btn v-if="showRowButton(header, item)"
            class="input-text-size ma-0 pa-0"
            style="height: 15px;"
            text :color="header.buttonColor || 'blue darken-1'"
            @click="handleButtonClick(item)"
          >{{header.buttonText || 'Button'}}</v-btn>
      </span>
      <!-- An icon in the cell that acts on its own row (the large payment grid's trash can). The
           header carries:
             icon            key into `icons` below, e.g. 'mdiDelete'
             tooltip         title of the live icon
             disableIf       (item) => Boolean - greys the icon out; it no longer acts
             disabledTooltip String, or (item) => String - why it is greyed out. Shown from a
                             wrapper span, because Vuetify gives a disabled icon
                             pointer-events: none and a tooltip on it would never open.
           The click / dblclick stops keep the click from also selecting the row or opening the
           edit dialog underneath it. Emits emitRowIconClick (item, header.value). -->
      <span v-else-if="header.formatter === 'iconButton'"
        :class="`crud-row-icon-cell crud-row-icon-${header.value}`"
        @click.stop
        @dblclick.stop
      >
        <v-tooltip v-if="isRowIconDisabled(header, item)" bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-icon small disabled tabindex="-1">{{ icons[header.icon] }}</v-icon>
            </span>
          </template>
          <span>{{ rowIconDisabledTooltip(header, item) }}</span>
        </v-tooltip>
        <v-icon v-else small tabindex="-1" :title="header.tooltip"
          @click="handleIconClick(header, item)">{{ icons[header.icon] }}</v-icon>
      </span>
      <!-- A dropdown rendered IN the cell, for a column the clerk changes without opening the
           row (the large payment grid's Type). The header carries:
             selectItems  [{ value, short, label }] - the closed control shows `short`, which is
                          all a grid column has room for, and the menu shows the full `label`
             lockEditIf   (item) => Boolean - renders the plain value instead, for the rows that
                          are not the clerk's to change (posted / voided)
             highlightIf  (item) => Boolean - tints the cell, for a value worth noticing
           The click / dblclick stops are what keep the cell from selecting the row or opening
           the edit dialog underneath it. The menu itself is rendered detached by v-menu, so
           picking an option never reaches the row either. -->
      <span v-else-if="header.formatter === 'inlineSelect'"
        @click.stop
        @dblclick.stop
      >
        <v-select
          v-if="!isInlineSelectLocked(header, item)"
          :value="resolveSubProp(header.value, item)"
          :items="header.selectItems || []"
          :style="{ width: header.selectWidth || '70px' }"
          :class="['crud-row-inline-select',
                   { 'crud-row-inline-select--alt': isInlineSelectHighlighted(header, item) }]"
          item-text="label"
          item-value="value"
          hide-details
          dense
          flat
          solo
          @change="emitInlineSelectChange(header, item, $event)"
        >
          <template v-slot:selection="{ item: option }">
            {{ option.short || option.label }}
          </template>
        </v-select>
        <span v-else class="crud-row-inline-select-locked">
          {{ inlineSelectDisplay(header, resolveSubProp(header.value, item)) }}
        </span>
      </span>
      <span v-else :style="{color: changeTxtColor(item, header)}">
        {{resolveSubProp(header.value, item)}}
      </span>
    </td>
    <td v-if="canDelete" @click.stop>
      <v-icon
        icon
        small class="mr-2"
        @click="deleteRowItem(item)"
      >
        {{icons.mdiDelete}}
      </v-icon>
    </td>
    <td v-if="canDownload" @click.stop>
      <v-icon
        icon
        small class="mr-2"
        @click="downloadRowItem(item)"
      >
        {{icons.mdiDownload}}
      </v-icon>
    </td>
  </tr>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import {
  mdiPencil,
  mdiDelete,
  mdiChevronDown,
  mdiChevronUp,
  mdiDownload,
} from '@mdi/js';
import {
  amountFormat, amountFormatCanbeEmpty,
  toLocalDateTime,
  toAuditDateTime,
  resolveSubProp,
  shortenCharacterFormat,
  pIdFormat,
  combinedNames,
  hideAndShowFields,
} from '../../../../util/shared/vue-global';
import {
  toShortDate,
  getFirstChar,
  boolToStr,
  flagToYorEmpty,
  propertyTaxTypeShortCode,
} from '../../../../util/shared/tmc-global';
import '../../../../assets/style.css';

import UploadFile from '../../UploadFile.vue';
// please don't use this widely throughout the project. It's
// specific logic for a specific screen, but I am exporting it
// for unit testing purposes.
export const autoDetectFormat = (valueStr = '') => {
  const value = (valueStr != null) ? valueStr.toString() : '';
  if (value.match(/Coordinated Universal Time/)) {
    return toAuditDateTime(valueStr);
  }
  if (value.match(/[0-9.,$]/) && value.match(/[.,$]/) && !value.match(/[a-zA-Z]/)) {
    const strIsCurrency = value.match(/[$]/) ? '$' : '';
    const bareAmount = value.replace(/[,$]*/g, '');
    return `${strIsCurrency}${amountFormat(bareAmount, true)}`;
  }
  return value;
};

export default {
  name: 'CRUDRow',
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    headers: {
      type: Array,
      default: () => ([]),
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canMove: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    canDownload: {
      type: Boolean,
      default: false,
    },
    uploadFileParams: {
      type: Object,
      default: () => ({}),
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => ([]),
    },
    formatPidPositions: {
      type: String,
      default: '',
    },
    showExpand: {
      type: Boolean,
      default: false,
    },
    disableKeyboardFunc: {
      type: Boolean,
      default: true,
    },
    variableToChangeRowBgColor: {
      type: Array,
      default: () => ([]),
    },
    hideCheckbox: {
      type: String,
      default: '',
    },
    baseCrudKey: {
      type: String,
      default: 'default',
    },
    items: {
      type: Array,
      default: () => ([]),
    },
  },
  components: {
    UploadFile,
  },
  data: () => ({
    icons: {
      mdiPencil,
      mdiDelete,
      mdiChevronDown,
      mdiChevronUp,
      mdiDownload,
    },
  }),
  computed: {
    ...mapGetters('base/crud', [
      'highlightedId',
    ]),
    headerList() {
      if (this.showSelect) {
        return this.headers.slice(1);
      }
      return this.headers;
    },
    selectedTask: {
      get() {
        return this.selectedItems || [];
      },
      set(value) {
        this.$emit('emitSelectedItem', value);
      },
    },
    getHighlightedId: {
      get() {
        return this.highlightedId(this.baseCrudKey) || '';
      },
      set(value) {
        this.setHighlightedId([this.baseCrudKey, value]);
      },
    },
  },
  methods: {
    resolveSubProp,
    amountFormat,
    amountFormatCanbeEmpty,
    toLocalDateTime,
    shortenCharacterFormat,
    pIdFormat,
    combinedNames,
    toShortDate,
    autoDetectFormat,
    getFirstChar,
    boolToStr,
    flagToYorEmpty,
    propertyTaxTypeShortCode,
    voidTransactionId(val, item) {
      if (!item.isVoid) return val || '';
      const id = val || '';
      if (!id) return '<span style="color:red;font-weight:bold;">VOID</span>';
      const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      if (id.length <= 16) return `${esc(id)}...<span style="color:red;font-weight:bold;">VOID</span>`;
      return `${esc(id.slice(0, 8))}...<span style="color:red;font-weight:bold;">VOID</span>...${esc(id.slice(-8))}`;
    },
    hideAndShowFields,
    // emitEditItem(item) {
    //   this.$emit('editItem', item);
    // },
    ...mapMutations('base/crud', [
      'setHighlightedId',
    ]),
    handleClickedItem(value) {
      this.$emit('emitClickedItem', value, 'fromClicked');
    },
    handleDisableCheckboxes(item) {
      const disabledStatus = item[this.hideCheckbox] || false;
      return disabledStatus;
    },
    handleTRCssStyles(item) {
      let backgroungClr = '';
      (this.variableToChangeRowBgColor || []).map((ft) => {
        if (item[ft.varName]) backgroungClr = ft.varCol || 'red';
        return '';
      });
      return { 'background-color': backgroungClr };
    },
    handleTRCssClasses(item, canEdit) {
      const classes = [];
      if (canEdit) classes.push('text-start ');
      // if (canEdit) classes.push('text-start clickable');
      else classes.push('text-start');
      if (this.getHighlightedId === item._id && this.disableKeyboardFunc) classes.push('selected-row-highlight');
      else classes.push('');
      // to add a class to last element
      const lastIndex = this.items.length ? this.items[this.items.length - 1]._id : '';
      if (lastIndex === item._id) classes.push('tr-last-index');
      return classes;
    },
    determineCssClasses(item, canEdit, header) {
      const classes = ['table-row-font-size', 'display-in-one-line'];
      if (item && item.crudRowCssAppend && Array.isArray(item.crudRowCssAppend)) {
        classes.push(...item.crudRowCssAppend);
      }
      if (canEdit) {
        classes.push('');
        // classes.push('clickable');
      }
      if (header && header.cellClass) classes.push(header.cellClass);
      if (header && header.align === 'right') {
        classes.push('text-align-right');
        classes.push('pointer');
      }
      if (header.align === 'center') classes.push('text-center');
      if (header.enableLine) classes.push('with-divider-left');
      return classes;
    },
    handleDoubleClick(item) {
      if (this.canEdit) this.$emit('editItem', item);
      if (this.canMove && !this.canEdit) this.$emit('editItem', item, 'canMove');
    },
    changeTxtColor(item, header) {
      if (header.formatterTwo === 'txtColorChange') {
        // to check for tax-due not 0 or not
        if (item.totalDue) return 'red';
      }
      if (header.formatterTwo === 'forteHighlight') {
        // Forte payments (POS/CC/E-Check) need to stand out from in-house/lockbox payments
        if (['fPOSPay', 'fCCPay', 'feCheckPay'].includes(item.paymentMethod)) return '#1565C0';
      }
      return '';
    },
    changeTheTextColor(item, header) {
      const checkItIsArrOrNot = header && header.changeTheTxtClrIfThisFieldsExits
        && header.changeTheTxtClrIfThisFieldsExits.length > 0
        ? header.changeTheTxtClrIfThisFieldsExits : [];
      return checkItIsArrOrNot.map((ft) => (item[ft] ? 'hightLightCRUDTextColor' : ''));
    },
    shouldTitleDisplay(val, chaLength = 20) {
      return (val && val.length > chaLength) ? val : '';
    },
    deleteRowItem(item) {
      this.$emit('emitDeleteRowItem', item);
    },
    downloadRowItem(item) {
      this.$emit('emitDownloadRowItem', item);
    },
    handleButtonClick(item) {
      this.$emit('emitRowButtonClick', item);
    },
    // A 'button' column can carry showButtonIf to hide itself on the rows it does not apply to.
    // A header without one behaves as it always has - the button shows on every row.
    showRowButton(header, item) {
      if (typeof header.showButtonIf !== 'function') return true;
      return !!header.showButtonIf(item);
    },
    // An 'iconButton' column is greyed out on the rows its disableIf says so. No gate means the
    // icon is live on every row.
    isRowIconDisabled(header, item) {
      if (typeof header.disableIf !== 'function') return false;
      return !!header.disableIf(item);
    },
    rowIconDisabledTooltip(header, item) {
      const { disabledTooltip } = header;
      return typeof disabledTooltip === 'function' ? disabledTooltip(item) : (disabledTooltip || '');
    },
    handleIconClick(header, item) {
      // the icon is only a live control while disableIf is off - guard the handler too
      if (this.isRowIconDisabled(header, item)) return;
      this.$emit('emitRowIconClick', item, header.value);
    },
    // Rows an 'inlineSelect' column is read-only on. No gate means every row is editable.
    isInlineSelectLocked(header, item) {
      if (typeof header.lockEditIf !== 'function') return false;
      return !!header.lockEditIf(item);
    },
    isInlineSelectHighlighted(header, item) {
      if (typeof header.highlightIf !== 'function') return false;
      return !!header.highlightIf(item);
    },
    /**
     * What a locked 'inlineSelect' cell reads: the same short form the closed dropdown shows on
     * the rows that are still editable, so the column lines up either way. A value with no
     * matching option is shown as it is rather than blanked.
     */
    inlineSelectDisplay(header, value) {
      const option = (header.selectItems || []).find((opt) => opt.value === value);
      if (option) return option.short || option.label;
      return (value === null || value === undefined) ? '' : value;
    },
    emitInlineSelectChange(header, item, value) {
      this.$emit('emitInlineSelectChange', item, header.value, value);
    },
  },
};
</script>

<style lang="sass">
.text-start .clickable:hover
  cursor: pointer
  font-weight: 600
.text-align-right
  text-align: right
.clickable-amount
  cursor: pointer
  color: #1565C0 !important
  text-decoration: underline
.table-row-font-size
  font-size: 0.75rem !important
// An 'inlineSelect' cell has to sit inside a dense grid row WITHOUT growing it. Vuetify's solo
// text-field is built for a form, not a table cell, and stacks its own minimums: the control
// carries min-height 38px even dense, the selections 32px, and the inner input 8px of vertical
// padding - which together turned a 19px row into 39px. Each one is flattened here so the cell
// is only as tall as the number in it.
.crud-row-inline-select
  font-size: 0.75rem !important
  line-height: 1 !important
  .v-input__control
    min-height: 0 !important
  .v-input__slot
    min-height: 0 !important
    padding: 0 0 0 2px !important
    margin-bottom: 0 !important
  // hide-details already drops this; belt and braces, since it is 14px of pure height
  .v-text-field__details
    display: none !important
  .v-select__selections
    min-height: 0 !important
    padding: 0 !important
    line-height: 1 !important
    input
      padding: 0 !important
      margin: 0 !important
      height: 16px !important
      max-height: 16px !important
  .v-input__append-inner
    margin-top: 0 !important
    padding-left: 0 !important
    align-self: center
  // the dropdown arrow is sized for a 40px field - at row height it only has to be findable
  .v-input__icon
    height: 16px !important
    width: 16px !important
    min-width: 16px !important
    .v-icon
      height: 14px !important
      width: 14px !important
      font-size: 14px !important
    svg
      height: 14px !important
      width: 14px !important
// amber, for a value worth noticing - a Type other than 1 Tax Payment on a payment line.
// An inset shadow rather than a border: a border would put 2px of height back on exactly the
// rows this is meant to call attention to, so they would not line up with the rest.
.crud-row-inline-select--alt
  .v-input__slot
    background-color: #fff8e1 !important
    box-shadow: inset 0 0 0 1px #ffa000 !important
    border-radius: 3px
.selected-row-highlight > td
  border-top: 2px solid #00ACC1 !important
  border-bottom: 2px solid #00ACC1 !important
.selected-row-highlight > td:first-child
  border-left: 2px solid #00ACC1 !important
.selected-row-highlight > td:last-child
  border-right: 2px solid #00ACC1 !important
</style>
